import type { BranchHierarchy, LegalEntityHierarchy } from "@/services/profileService";

export type BillingAgencyIdSource = "root" | "parent";

export function getBillingAgencyIdSource(): BillingAgencyIdSource {
  return import.meta.env.VITE_BILLING_AGENCY_ID_SOURCE === "root" ? "root" : "parent";
}

export function getBillingEnabledLegalEntityIds() {
  return (import.meta.env.VITE_BILLING_ENABLED_LEGAL_ENTITY_IDS ?? "")
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function findLegalEntity(entities: LegalEntityHierarchy[], id: string | null): LegalEntityHierarchy | null {
  if (!id) return null;

  for (const entity of entities) {
    if (entity.id === id) return entity;
    const found = findLegalEntity(entity.subEntities ?? [], id);
    if (found) return found;
  }

  return null;
}

export function findBranch(entities: LegalEntityHierarchy[], id: string | null): BranchHierarchy | null {
  if (!id) return null;

  for (const entity of entities) {
    const branch = (entity.branches ?? []).find((item) => item.id === id);
    if (branch) return branch;
    const found = findBranch(entity.subEntities ?? [], id);
    if (found) return found;
  }

  return null;
}

export function findRootLegalEntityForSelection(
  entities: LegalEntityHierarchy[],
  legalEntityId: string | null,
  branchId: string | null,
): LegalEntityHierarchy | null {
  for (const entity of entities) {
    if (entity.id === legalEntityId || (branchId && entity.branches?.some((branch) => branch.id === branchId))) {
      return entity;
    }

    const found = findRootLegalEntityForSelection(entity.subEntities ?? [], legalEntityId, branchId);
    if (found) return entity;
  }

  return null;
}

export function resolveBillingAgencyId(params: {
  source: BillingAgencyIdSource;
  legalEntityId: string | null;
  branchId: string | null;
  legalEntities: LegalEntityHierarchy[];
  profileContextLoaded: boolean;
}) {
  const { source, legalEntityId, branchId, legalEntities, profileContextLoaded } = params;

  if ((legalEntityId || branchId) && !profileContextLoaded) return null;

  if (source === "parent") {
    const selectedBranch = findBranch(legalEntities, branchId);
    return selectedBranch?.legalEntityId ?? legalEntityId ?? branchId ?? null;
  }

  const rootLegalEntity = findRootLegalEntityForSelection(legalEntities, legalEntityId, branchId);
  return rootLegalEntity?.id ?? legalEntityId ?? branchId ?? null;
}

export function isBillingAllowedForAgencyId(agencyId: string | null) {
  const allowedIds = getBillingEnabledLegalEntityIds();
  if (allowedIds.length === 0) return true;
  return Boolean(agencyId && allowedIds.includes(agencyId));
}

export function canShowBillingForSelection(params: {
  legalEntityId: string | null;
  branchId: string | null;
  legalEntities: LegalEntityHierarchy[];
  profileContextLoaded: boolean;
}) {
  const agencyId = resolveBillingAgencyId({
    source: getBillingAgencyIdSource(),
    ...params,
  });

  return isBillingAllowedForAgencyId(agencyId);
}
